import React, { RefObject, useEffect, useRef } from 'react'
import { Form, FormGroup } from 'react-bootstrap'
import { useFormikContext } from 'formik'
import { useServiceContext } from '~/root'
import { useTranslation } from 'react-i18next'
import useNumericOnly from '~/hooks/useNumberOnly'
import { WF_STEP_PROCESSING_TYPE } from '~/types/workflow'
import CustomErrorMessage from '~/components/ErrorMessage'
import { Select } from '~/components/Select'
import SelectUserGroup from '~/components/SelectUserGroup'
import SelectStepLabel from './steps/SelectStepLabel'

interface PrettyPrintProps {
  json: unknown
  debugMode?: boolean
}
const PrettyPrint: React.FC<PrettyPrintProps> = ({ json, debugMode }) => {
  return <div>{debugMode && <pre>{JSON.stringify(json, null, 2)}</pre>}</div>
}

export interface UserGroupStep {
  processingType: string
  taskTeamStepLabel: any
  nofAllValue: string | null
  usersGroups: any[]
}

interface UserGroupStepsProps {
  arrayHelpers: {
    name: string
    push: (step: UserGroupStep) => void
    remove: (index: number) => void
    replace: (index: number, step: UserGroupStep) => void
  }
  values: {
    steps: UserGroupStep[]
  }
  isFromManageWFModals?: boolean
  labelTxt?: string
  nofallTxt?: string
  disabled?: boolean
}

function UserGroupSteps(props: UserGroupStepsProps) {
  // todo, check for arrayHelpers and values in props!
  const { arrayHelpers, values, isFromManageWFModals } = props
  const formikContext = useFormikContext()

  const numInputRef = useRef<HTMLInputElement>(null)
  const { t: translate } = useTranslation()
  // useNumericOnly expects RefObject<HTMLInputElement> | null
  useNumericOnly(props.isFromManageWFModals ? numInputRef : null, 10)

  const orderTypes = [
    { value: WF_STEP_PROCESSING_TYPE.SERIAL, label: translate('ONE_AT_A_TIME') },
    { value: WF_STEP_PROCESSING_TYPE.PARALLEL, label: translate('ALL_AT_ONCE') },
    { value: WF_STEP_PROCESSING_TYPE.N_OF_ALL, label: translate('N_OF_ALL') }
  ]

  const addStep = React.useCallback(() => {
    const newStep: UserGroupStep = {
      processingType: WF_STEP_PROCESSING_TYPE.SERIAL,
      taskTeamStepLabel: null,
      nofAllValue: '',
      usersGroups: []
    }
    props.arrayHelpers.push(newStep)
  }, [props.arrayHelpers])

  useEffect(() => {
    if (props.values.steps.length < 1) {
      addStep()
    }
  }, [])

  const removeStep = React.useCallback(
    (index: number) => {
      props.arrayHelpers.remove(index)
    },
    [props.arrayHelpers]
  )

  const updateStep = React.useCallback(
    (newData: UserGroupStep, index: number) => {
      props.arrayHelpers.replace(index, newData)
      props.isFromManageWFModals ? formikContext.validateForm() : null
    },
    [props.arrayHelpers, props.isFromManageWFModals, formikContext]
  )

  return (
    <FormGroup>
      <PrettyPrint debugMode={false} json={values.steps} />
      <Form.Label>{translate(props?.labelTxt ?? '')}</Form.Label>
      <div className="custom-approvers-table">
        {values.steps.length ? (
          <div className="table-header">
            <span className="h steps ta-center">{translate('STEP')}</span>
            <span className="h customlabel">{translate('CR_CUSTOM_STEP_LABEL')}</span>
            <span className="h usersgroups">{translate('USERS_GROUPS')}</span>
            <span className="h order">{translate('ORDER')}</span>
          </div>
        ) : null}
        {props?.values?.steps?.map?.((item: any, index: number) => (
          <div key={index} className="table-row py5">
            <span className="h steps ta-center py5">{index + 1}</span>
            <span className="h customlabel">
              <SelectStepLabel
                name={`${arrayHelpers.name}.${index}.taskTeamStepLabel`}
                onChange={(option) => {
                  item.taskTeamStepLabel = option
                  updateStep(item, index)
                }}
                value={item.taskTeamStepLabel}
                isDisabled={props.disabled}
              />
            </span>
            <span className="h usersgroups">
              <SelectUserGroup
                placeholder={translate('SELECT_USERS/GROUPS...')}
                isMulti={true}
                value={item.usersGroups}
                onChange={(option) => {
                  const newValue = option ? option : []
                  if (
                    !window._ || !window._.isEqual
                      ? JSON.stringify(item.usersGroups) !== JSON.stringify(newValue)
                      : !window._.isEqual(item.usersGroups, newValue)
                  ) {
                    item.usersGroups = newValue
                    updateStep(item, index)
                  }
                }}
                name={`${arrayHelpers.name}.${index}.usersGroups`}
                isDisabled={props.disabled}
              />
              <CustomErrorMessage name={`${arrayHelpers.name}.${index}.usersGroups`} />
            </span>
            <span className="h order">
              <Select
                name={`${arrayHelpers.name}.${index}.processingType`}
                value={item.processingType}
                options={orderTypes}
                isClearable={false}
                onChange={(
                  option:
                    | import('react-select').SingleValue<{ value: string }>
                    | import('react-select').MultiValue<{ value: string }>,
                  _actionMeta
                ) => {
                  // Only handle single value for processingType
                  if (option && !Array.isArray(option) && 'value' in option) {
                    item.processingType = option.value
                  } else {
                    item.processingType = ''
                  }
                  if (item.processingType !== WF_STEP_PROCESSING_TYPE.N_OF_ALL && props.isFromManageWFModals) {
                    item.nofAllValue = ''
                  }
                  updateStep(item, index)
                }}
                showNewOptionAtTop={true}
                searchable
                id="795f0a8a-f8b3-47d8-81ca-b3b3ab065e27"
                isDisabled={props.disabled}
              />
              <CustomErrorMessage name={`${arrayHelpers.name}.${index}.processingType`} />
              {item.processingType === WF_STEP_PROCESSING_TYPE.N_OF_ALL ? (
                <div className="nofall-wrapper hform-group">
                  <input
                    ref={numInputRef}
                    disabled={props.disabled}
                    readOnly={props.disabled}
                    type="number"
                    name={`${arrayHelpers.name}.${index}.nofAllValue`}
                    onChange={(e) => {
                      item.nofAllValue = e.target.value
                      updateStep(item, index)
                    }}
                    value={item.nofAllValue || ''}
                    className="md-input nofall-input"
                  />{' '}
                  {translate(props.nofallTxt || '')}
                  <CustomErrorMessage name={`${arrayHelpers.name}.${index}.nofAllValue`} />
                </div>
              ) : null}
            </span>
            {!props.disabled && (
              <a
                className="close"
                onClick={(e) => {
                  e.preventDefault()
                  removeStep(index)
                }}
              ></a>
            )}
          </div>
        ))}
      </div>

      {!props.disabled && (
        <button
          data-action={'add-step'}
          className="btn btn-chita btn-add-step btn-second at_btnAddStepApprover"
          type="button"
          onClick={addStep}
        >
          {translate('ADD_STEP')}
        </button>
      )}
    </FormGroup>
  )
}

export default UserGroupSteps
